import { checkNextPage } from '@/shared/lib/utils/pagination'
import i18n from '@/app/i18n'
import { TOAST_DISPATCHERS } from '@/shared/constants/store-constants/toast'
import massPaymentsHistoryService from '@/shared/services/mass-payments-history.service'
import massPaymentsService from '@/shared/services/mass-payments.service'
import { MASS_PAYMENTS_HISTORY_DISPATCHERS } from '@/shared/constants/store-constants/massPaymentsHistory'

export default {
  onSearch({ commit, dispatch }, payload) {
    commit('SET_SEARCH', payload)
    dispatch(
      MASS_PAYMENTS_HISTORY_DISPATCHERS.resetStore,
      { resetSort: false },
      { root: true }
    )
    dispatch(
      MASS_PAYMENTS_HISTORY_DISPATCHERS.goToLoadMassPaymentsHistory,
      { search: payload },
      { root: true }
    )
  },

  resetSearch({ commit, dispatch }) {
    commit('RESET_SEARCH')
    dispatch(
      MASS_PAYMENTS_HISTORY_DISPATCHERS.goToLoadMassPaymentsHistory,
      {},
      { root: true }
    )
  },

  resetStore({ commit }) {
    commit('RESET_STORE')
  },

  resetPage({ commit }) {
    commit('RESET_PAGE')
  },

  changePage({ commit, dispatch, state }) {
    if (checkNextPage(state.page, state.total, state.limit)) {
      commit('SET_PAGE', state.page + 1)

      dispatch(
        MASS_PAYMENTS_HISTORY_DISPATCHERS.goToLoadMassPaymentsHistory,
        {},
        { root: true }
      )
    }
  },

  onSelectSort({ commit, dispatch }, payload) {
    dispatch(
      MASS_PAYMENTS_HISTORY_DISPATCHERS.resetStore,
      { resetSort: false },
      { root: true }
    )
    commit('SET_SORTING', payload)
    dispatch(
      MASS_PAYMENTS_HISTORY_DISPATCHERS.goToLoadMassPaymentsHistory,
      {},
      { root: true }
    )
  },

  async goToLoadMassPaymentsHistory({ commit, state, dispatch }, params) {
    try {
      commit('SET_LOADING', true)

      const response = await massPaymentsHistoryService.getHistory({
        ...params,
        page: state.page,
        limit: state.limit,
        ...(state.search && { search: state.search }),
        sort_by: state.sort.sort_by,
        sort_direction: state.sort.sort_direction,
      })

      commit('SET_ITEMS', response)
    } catch (error) {
      if (!error.isHandled) {
        dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async goToCopyMassPayment({ commit, dispatch }, paymentId) {
    try {
      commit('SET_LOADING', true)

      const payload = await massPaymentsService.copyMassPayment(paymentId)

      dispatch(
        TOAST_DISPATCHERS.setSuccessMessage,
        {
          title: i18n.global.t('PaymentLinks | toast | copied | title'),
          message: '',
        },
        { root: true }
      )

      return { success: true, payload }
    } catch (error) {
      if (!error.isHandled) {
        dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      }
      return { success: false }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async goToLoadMassPaymentDetail({ commit, state, dispatch }, paymentId) {
    try {
      commit('SET_DETAIL_LOADING', true)

      const params = {
        search: state.detailSearch,
        ...state.filters,
        page: state.detailPage,
        limit: state.detailLimit,
      }

      const { data } = await massPaymentsService.getDetailMassPayment(
        paymentId,
        params
      )

      commit('SET_DETAIL_FORM_DATA', data.mass_payment_info)
      commit('SET_DETAIL_ITEMS', {
        items: data.items,
        total: data.total,
        reset: state.detailPage === 1,
      })

      return { success: true, data }
    } catch (error) {
      if (!error.isHandled) {
        dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      }
      return { success: false }
    } finally {
      commit('SET_DETAIL_LOADING', false)
    }
  },

  onSearchDetail({ commit, dispatch }, { paymentId, searchValue }) {
    commit('SET_DETAIL_SEARCH', searchValue)
    commit('CLEAR_DETAIL_ITEMS')
    commit('RESET_DETAIL_PAGE')

    dispatch('goToLoadMassPaymentDetail', paymentId)
  },

  onSelectDetailFilter({ commit, dispatch }, { paymentId, key, payload }) {
    commit('SET_FILTER', { key, payload })
    commit('CLEAR_DETAIL_ITEMS')
    commit('RESET_DETAIL_PAGE')

    dispatch('goToLoadMassPaymentDetail', paymentId)
  },

  async onLoadMoreDetail({ commit, state, dispatch }, paymentId) {
    const nextPage = state.detailPage + 1
    const hasMore = state.detailItems.length < state.detailTotal

    if (!hasMore || state.detailLoading) {
      return
    }

    commit('SET_DETAIL_PAGE', nextPage)
    await dispatch('goToLoadMassPaymentDetail', paymentId)
  },

  async downloadHistoryReport(
    { dispatch },
    { paymentId, language = i18n.global.locale }
  ) {
    try {
      const response = await massPaymentsHistoryService.downloadHistoryReport(
        paymentId,
        {
          language,
        }
      )

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url

      const contentDisposition = response.headers['content-disposition']
      let filename = `mass-payment-${paymentId}-report.xlsx`

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      dispatch(TOAST_DISPATCHERS.setSuccessMessage, {}, { root: true })

      return { success: true }
    } catch (error) {
      if (!error.isHandled) {
        dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      }
      return { success: false }
    }
  },
}
